import type { TableInformation } from '../../../config/flavourConfig';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import type {
    TermDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type {
    PrhHolidayHomeDTO,
    PrhHouseDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import type { CaravanDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/uvp';
import { getDogInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/dogCoverableDetailsInsuranceSumsConfig';
import { getCatInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/catCoverableDetailsInsuranceSumsConfig';
import { getKennelInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/kennelCoverableDetailsInsuranceConfig';
import { getAdultAccidentInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/adultAccidentCoverableDetailsInsuranceSumsConfig';
import { getYoungAccidentInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/youngAccidentCoverableDetailsInsuranceSumsConfig';
import { getChildAccidentInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/childAccidentCoverableDetailsInsuranceSumsConfig';
import { getYouthContentInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/youthContentCoverableDetailsInsuranceSumsConfig';
import { getPrivateContentInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/privateContentCoverableDetailsInsuranceSumsConfig';
import {
    getPrivateCarInsuranceSumsCoverableDetailsConfig,
    getCaravanInsuranceSumsCoverableDetailsConfig
} from '../../ShoppingBasketPage/utils/ShoppingBasketInsuranceSumsUtil';
import { getPrhHouseInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/privateHouseCoverableDetailsInsuranceSumsConfig';
import { getPrhHHInsuranceSumsCoverableDetailsConfig } from './LoBInsuranceSumsConfig/privateHolidayHomeCoverableDetailsInsuranceSumsConfig';
import { FLAVOUR_NAME } from '../../../constants/coverable';

const getInsuranceSumsCoverableDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case FLAVOUR_NAME.DOG:
            return getDogInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.CAT:
            return getCatInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.KENNEL:
            return getKennelInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.ACC_ACCIDENT:
            return getAdultAccidentInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.ACC_YOUNG:
            return getYoungAccidentInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.ACC_CHILD:
            return getChildAccidentInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.PRC_YOUTH_CONTENT:
            return getYouthContentInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.PRC_CONTENT:
            return getPrivateContentInsuranceSumsCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.MVP_CAR:
            return getPrivateCarInsuranceSumsCoverableDetailsConfig(coverable as CarDTO);
        case FLAVOUR_NAME.PRH_HOUSE:
            return getPrhHouseInsuranceSumsCoverableDetailsConfig(
                coverable as PrhHouseDTO,
                getHippoText
            );
        case FLAVOUR_NAME.PRH_HOLIDAY_HOME:
            return getPrhHHInsuranceSumsCoverableDetailsConfig(
                coverable as PrhHolidayHomeDTO,
                getHippoText
            );
        case FLAVOUR_NAME.UVP_CARAVAN:
            return getCaravanInsuranceSumsCoverableDetailsConfig(coverable as CaravanDTO);
        default:
            return undefined;
    }
};

const getCoverageTermValue = (
    coverable: CoverableDTO,
    coverageType: string,
    termId: string
): string => {
    return (
        coverable.coverages
            ?.find((coverage) => coverage.type === coverageType)
            ?.terms?.find((term: TermDTO) => term.publicId === termId)?.value ?? ''
    );
};

const formatAmountToDK = (amount: string): string => {
    return parseInt(amount).toLocaleString('da-DK');
};

const formatAmountWithCurrency = (amount: string): string => {
    return amount?.concat(' kr.');
};

const isCoverageSelected = (coverable: CoverableDTO, coverageType: string): boolean => {
    return (
        coverable.coverages?.find((coverage) => coverage.type === coverageType)?.selected ?? false
    );
};

export {
    getInsuranceSumsCoverableDetailsConfig,
    getCoverageTermValue,
    formatAmountToDK,
    formatAmountWithCurrency,
    isCoverageSelected
};
